import { useEffect, useState } from 'react';
import { Title } from 'react-admin';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { Tile, TileGrid } from '../dashboard/Tile';
import { buildListUrl } from '../dashboard/listUrl';
import { menuLabel } from '../menuConfig';
import { FeaturesLoading, useFeatureState } from '../featureFlags/FeatureGate';
import { useHasPermission } from '../PermissionGate';
import { fmtDateTimeShort } from '../../shared/listCells';
import {
  FEATURE_TRADE_DASHBOARD,
  TRADE_TILE_TEXT,
  fetchTradeDashboard,
  type TradeDashboardSummary,
} from './tradeDashboardApi';
import { MOCKUP_CLASS } from '../../mockupLook/tokens';

/**
 * Pulpit Trade (makieta back office v2, A9; przełącznik `tradeDashboard`,
 * 20.10.2026) — liczby, od których trader zaczyna dzień. Każda prowadzi do
 * listy zleceń z filtrem, którym API ją policzyło (kafelek bierze filtr
 * z odpowiedzi, nie składa go sam). Kafelek funkcji wyłączonej nie
 * przychodzi wcale.
 *
 * Makieta ma też „Kandydaci proaktywni” i „Czeka na Sales” — tych nie ma:
 * propozycja przejęcia za zgodą Team Leadera przeczy odpowiedziom Marathonu
 * (07.10 i 16.10), a Sales nie pracuje w aplikacji (decyzja D3 otwarta).
 */

export const FEATURE_OFF_NOTE =
  'Pulpit Trade jest wyłączony w wersji aplikacji, z której korzystasz.';
export const NO_PERMISSION_NOTE = 'Pulpit Trade jest dla działu Trade.';
export const LOAD_ERROR = 'Nie udało się wczytać pulpitu Trade. Odśwież stronę.';

export const TradeDashboard = () => {
  const on = useFeatureState(FEATURE_TRADE_DASHBOARD);
  const canTrade = useHasPermission('TRADE');
  if (on === undefined) return <FeaturesLoading />;
  if (!on || !canTrade) {
    return (
      <Box sx={{ p: 2 }}>
        <Title title={menuLabel('trade-dashboard')} />
        <Alert severity="info">{on ? NO_PERMISSION_NOTE : FEATURE_OFF_NOTE}</Alert>
      </Box>
    );
  }
  return <TradeDashboardScreen />;
};

const TradeDashboardScreen = () => {
  const [summary, setSummary] = useState<TradeDashboardSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    fetchTradeDashboard()
      .then((s) => {
        if (active) setSummary(s);
      })
      .catch(() => {
        if (active) setError(LOAD_ERROR);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <Stack spacing={2} sx={{ p: 2 }} data-testid="trade-dashboard">
      <Title title={menuLabel('trade-dashboard')} />
      {error ? <Alert severity="error">{error}</Alert> : null}
      <Box>
        <Typography variant="h6" gutterBottom className={MOCKUP_CLASS.sectionTitle}>
          Trade — zlecenia
        </Typography>
        <TileGrid>
          {(summary?.tiles ?? []).map((t) => {
            const text = TRADE_TILE_TEXT[t.key] ?? { label: t.key, description: '' };
            return (
              <Tile
                key={t.key}
                label={text.label}
                value={t.count}
                description={text.description}
                linkTo={buildListUrl('user-offers', t.filter)}
                highlight={t.count > 0 ? text.highlight : undefined}
                testId={`trade-tile-${t.key}`}
              />
            );
          })}
          {!summary && !error ? <Tile label="Wczytuję…" loading /> : null}
        </TileGrid>
      </Box>
      {summary?.computedAt ? (
        <Typography variant="caption" color="text.secondary">
          Stan na {fmtDateTimeShort(summary.computedAt)} — liczby to te same filtry co lista zleceń.
        </Typography>
      ) : null}
    </Stack>
  );
};
